import type { AgencyDataFillRow } from './agencyDataFillRows';

/**
 * The agencies somebody has worked on lately.
 *
 * Replaces a shortlist people would have to curate -- favourite it, unfavourite
 * it, hide it -- with one nobody has to touch. An agency is recent because it
 * was read or filled, which is the same evidence favouriting would stand in
 * for, and it drops off on its own.
 *
 * It matters more than it looks: the list holds every agency, not only the
 * Kinnser ones, so "all" is long enough that finding the one you were on this
 * morning means scrolling or knowing its name.
 */

/** The nightly sync. It reads every Kinnser agency, so it is not evidence
 *  that a person was working on any of them. */
const SCHEDULED = 'schedule';

/** When a person last read this agency from Kinnser or filled it, or null. */
export function lastWorkedOn(row: AgencyDataFillRow): string | null {
  const moments = [
    // Asking Kinnser is working on the agency too, and it is the first thing
    // anybody does -- but only when a person asked. The nightly sync stamps
    // fetched_at on every Kinnser agency, so counting it made all of them
    // recent by morning and left the filter showing the list it exists to
    // shorten.
    row.fetchSource === SCHEDULED ? null : row.fetchedAt,
    row.appliedAt,
  ].filter((moment): moment is string => Boolean(moment));
  if (moments.length === 0) return null;
  // ISO-8601 in the same zone sorts lexicographically, which is what the API
  // returns, so this needs no parsing to find the latest.
  return moments.reduce((latest, moment) =>
    moment > latest ? moment : latest
  );
}

/**
 * The windows tried, in order, and the shortest one that holds enough.
 *
 * A fixed window is wrong at both ends: a quiet fortnight empties the list, and
 * a busy week buries what you did this morning under forty rows. So the window
 * grows until the list is worth having and then stops.
 */
export const RECENT_WEEKS = [1, 2, 3, 8, 16] as const;
const ENOUGH = 10;

export interface Recents {
  weeks: number;
  agencyIds: Set<string>;
}

export function recentAgencies<Row extends { agencyId: string }>(
  rows: readonly Row[],
  now: Date,
  /**
   * When this row was last worked on.
   *
   * Required, and not defaulted to one screen's rule: a caller that forgot to
   * pass its own used to compile and get a permanently empty Recent list,
   * because the default read fields its rows do not have.
   */
  workedOn: (row: Row) => string | null
): Recents {
  const worked = rows
    .map(row => ({ id: row.agencyId, at: workedOn(row) }))
    .filter((entry): entry is { id: string; at: string } => entry.at !== null);

  for (const weeks of RECENT_WEEKS) {
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - weeks * 7);
    const within = worked.filter(entry => new Date(entry.at) >= cutoff);
    // The last window is the widest there is, so it is the answer whether or
    // not it holds enough.
    if (
      within.length >= ENOUGH ||
      weeks === RECENT_WEEKS[RECENT_WEEKS.length - 1]
    ) {
      return { weeks, agencyIds: new Set(within.map(entry => entry.id)) };
    }
  }
  return { weeks: RECENT_WEEKS[0], agencyIds: new Set() };
}
